// Pisos (16×16). Variações são sorteadas por hash da posição: o piso não fica repetido.
// GABARITO da Onda 0: madeira, porcelanato, carpete, grama, calçada, asfalto. Pisos novos
// (cerâmica de banheiro, piso de servidor, deck, xadrez da copa) podem entrar com H6/H7.

import { Pix } from './pix.js';
import type { TileDef } from './types.js';

/** Tábuas de madeira na horizontal, 4 px cada, com emendas desencontradas. */
function planks(seed: number): Pix {
  const pix = new Pix(16, 16).rect(0, 0, 16, 16, 'W');
  for (let row = 0; row < 4; row++) {
    const y = row * 4;
    pix.hline(0, y + 3, 16, 'D'); // junta entre tábuas
    const seam = (row * 7 + seed * 5) % 16;
    pix.vline(seam, y, 3, 'D');
    // veio da madeira: um risco claro curto por tábua
    const grain = (seam + 4 + row * 3) % 12;
    pix.hline(grain, y + 1, 3, 'L');
  }
  if (seed === 1) pix.px(5, 6, 'D').px(6, 6, 'D');
  if (seed === 2) pix.px(11, 10, 'D');
  return pix;
}

function porcelain(): Pix {
  return new Pix(16, 16).box(0, 0, 16, 16, { fill: 'b', light: 'l', dark: 'd' }).px(3, 3, 'l').px(4, 3, 'l');
}

function carpet(seed: number): Pix {
  const pix = new Pix(16, 16).rect(0, 0, 16, 16, 'c');
  for (let i = 0; i < 10; i++) pix.px((i * 7 + seed * 3) % 16, (i * 5 + seed) % 16, i % 2 ? 'l' : 'd');
  return pix;
}

function grass(seed: number): Pix {
  const pix = new Pix(16, 16).rect(0, 0, 16, 16, 'g');
  for (let i = 0; i < 9; i++) {
    const x = (i * 5 + seed * 7) % 15;
    const y = (i * 11 + seed * 3) % 14;
    pix.px(x, y + 1, 'd').px(x + 1, y, 'G');
  }
  if (seed === 2) pix.px(7, 7, 'f').px(8, 7, 'f');
  return pix;
}

function sidewalk(): Pix {
  return new Pix(16, 16).box(0, 0, 16, 16, { fill: 'b', light: 'l', dark: 'd' }).hline(0, 8, 16, 'd').vline(8, 0, 8, 'd');
}

function asphalt(seed: number): Pix {
  const pix = new Pix(16, 16).rect(0, 0, 16, 16, 'b');
  for (let i = 0; i < 6; i++) pix.px((i * 9 + seed * 5) % 16, (i * 7 + seed * 3) % 16, i % 2 ? 'l' : 'd');
  return pix;
}

/** Meio-fio: calçada na metade de cima, quina clara do meio-fio e face escura embaixo. */
function kerb(): Pix {
  return new Pix(16, 16)
    .rect(0, 0, 16, 10, 'b')
    .hline(0, 10, 16, 'l')
    .rect(0, 11, 16, 5, 'd')
    .hline(0, 15, 16, 'o');
}

/** Faixa de pedestre: listras brancas de 4 px a cada 8 px (o padrão repete sem costura). */
function crosswalk(): Pix {
  const pix = new Pix(16, 16).rect(0, 0, 16, 16, 'b');
  for (const x of [0, 8]) pix.rect(x, 0, 4, 16, 'w').vline(x, 0, 16, 'W').vline(x + 3, 0, 16, 'x');
  for (let i = 0; i < 4; i++) pix.px((i * 11 + 3) % 16, (i * 7 + 5) % 16, 'l');
  return pix;
}

/** Asfalto com linha central amarela tracejada (tracejado de 8 px a cada 16). */
function laneLine(seed: number): Pix {
  const pix = new Pix(16, 16).rect(0, 0, 16, 16, 'b');
  for (let i = 0; i < 6; i++) pix.px((i * 9 + seed * 5) % 16, (i * 7 + seed * 3) % 16, i % 2 ? 'l' : 'd');
  pix.rect(0, 7, 8, 2, 'Y').hline(0, 7, 8, 'y');
  return pix;
}

/** Terra batida: manchas escuras e claras e algumas pedrinhas. */
function dirt(seed: number): Pix {
  const pix = new Pix(16, 16).rect(0, 0, 16, 16, 'b');
  for (let i = 0; i < 12; i++) pix.px((i * 7 + seed * 3) % 16, (i * 5 + seed * 11) % 16, i % 3 ? 'd' : 'l');
  pix.px((seed * 4 + 2) % 16, (seed * 6 + 9) % 16, 's');
  return pix;
}

/** Pedra de praça em tijolo corrido: juntas em 8 px, alternadas de uma fiada para outra. */
function plaza(): Pix {
  const pix = new Pix(16, 16).rect(0, 0, 16, 16, 'b');
  pix.hline(0, 7, 16, 'd').hline(0, 15, 16, 'd');
  pix.vline(7, 0, 7, 'd').vline(15, 0, 7, 'd').vline(3, 8, 7, 'd').vline(11, 8, 7, 'd');
  pix.hline(0, 0, 16, 'l').hline(0, 8, 16, 'l');
  pix.px(2, 4, 'l').px(12, 12, 'l');
  return pix;
}

/** Ceramica de banheiro: branco com a junta na borda direita e de baixo, sem costura. */
function bathroomTile(): Pix {
  return new Pix(16, 16).box(0, 0, 16, 16, { fill: 'w', light: 'W', dark: 'x' }).px(3, 3, 'W');
}

/** Xadrez da copa: quatro quadrados de 8 px, alternados e com brilho no canto. */
function checker(): Pix {
  const pix = new Pix(16, 16);
  pix.rect(0, 0, 8, 8, 'K').rect(8, 0, 8, 8, 'L').rect(0, 8, 8, 8, 'L').rect(8, 8, 8, 8, 'K');
  pix.px(1, 1, 'k').px(9, 9, 'k');
  pix.hline(8, 0, 8, 'W').hline(0, 8, 8, 'W');
  return pix;
}

/** Piso elevado de servidor: placa de metal com perfurações. */
function serverFloor(): Pix {
  return new Pix(16, 16)
    .box(0, 0, 16, 16, { fill: 'm', light: 'M', dark: 'n' })
    .rect(4, 4, 2, 2, 'k')
    .rect(10, 10, 2, 2, 'k');
}

/**
 * Deck de madeira externa: tábuas largas na horizontal (7 px), frestas escuras finas e pregos.
 * Sem emendas nem juntas desencontradas, para não lembrar tijolo.
 */
function deckBoards(seed: number): Pix {
  const pix = new Pix(16, 16);
  for (const top of [0, 8]) {
    pix.hline(0, top, 16, 'L'); // borda de cima, com luz
    pix.rect(0, top + 1, 16, 5, 'W'); // corpo da tábua
    pix.hline(0, top + 6, 16, 'D'); // borda de baixo, em sombra
    pix.hline(0, top + 7, 16, 'g'); // fresta escura
    // veio: dois riscos curtos por tábua, em posições que mudam com a semente
    pix.hline(((seed * 5 + top) % 9) + 2, top + 2, 5, 'L');
    pix.hline(((seed * 7 + top * 3) % 8) + 4, top + 4, 4, 'D');
    pix.px(3, top + 3, 'n').px(12, top + 3, 'n'); // pregos
  }
  return pix;
}

const WOOD = { D: 'oak1', W: 'oak2', L: 'oak3' };
const DECK = { W: 'oak2', L: 'oak3', D: 'oak1', g: 'wood0', n: 'metal3' };

export const TILES: readonly TileDef[] = [
  { id: 'madeira', name: 'Piso de madeira', sprite: planks(0).sprite(WOOD), variants: [planks(1).sprite(WOOD), planks(2).sprite(WOOD)] },
  { id: 'porcelanato', name: 'Porcelanato', sprite: porcelain().sprite({ b: 'tile2', l: 'tile3', d: 'tile1' }) },
  {
    id: 'carpete',
    name: 'Carpete',
    sprite: carpet(0).sprite({ c: 'carpet2', l: 'carpet3', d: 'carpet1' }),
    variants: [carpet(1).sprite({ c: 'carpet2', l: 'carpet3', d: 'carpet1' })],
  },
  {
    id: 'grama',
    name: 'Grama',
    sprite: grass(0).sprite({ g: 'grass2', d: 'grass1', G: 'grass3', f: 'gold3' }),
    variants: [1, 2].map((s) => grass(s).sprite({ g: 'grass2', d: 'grass1', G: 'grass3', f: 'gold3' })),
  },
  { id: 'calcada', name: 'Calçada', sprite: sidewalk().sprite({ b: 'concrete2', l: 'concrete3', d: 'concrete1' }) },
  {
    id: 'asfalto',
    name: 'Asfalto',
    sprite: asphalt(0).sprite({ b: 'asphalt2', l: 'asphalt3', d: 'asphalt1' }),
    variants: [asphalt(1).sprite({ b: 'asphalt2', l: 'asphalt3', d: 'asphalt1' })],
  },
  { id: 'meio-fio', name: 'Meio-fio', sprite: kerb().sprite({ b: 'concrete2', l: 'concrete3', d: 'concrete1', o: 'concrete0' }) },
  { id: 'faixa-pedestre', name: 'Faixa de pedestre', sprite: crosswalk().sprite({ b: 'asphalt2', w: 'white2', W: 'white3', x: 'white1', l: 'asphalt3' }) },
  {
    id: 'asfalto-faixa',
    name: 'Asfalto com faixa',
    sprite: laneLine(0).sprite({ b: 'asphalt2', l: 'asphalt3', d: 'asphalt1', Y: 'light2', y: 'light3' }),
    variants: [laneLine(1).sprite({ b: 'asphalt2', l: 'asphalt3', d: 'asphalt1', Y: 'light2', y: 'light3' })],
  },
  { id: 'terra', name: 'Terra', sprite: dirt(0).sprite({ b: 'trunk2', d: 'trunk1', l: 'trunk3', s: 'concrete3' }), variants: [dirt(1).sprite({ b: 'trunk2', d: 'trunk1', l: 'trunk3', s: 'concrete3' })] },
  { id: 'pedra-praca', name: 'Pedra de praça', sprite: plaza().sprite({ b: 'concrete2', l: 'concrete3', d: 'concrete1' }) },
  { id: 'deck', name: 'Deck', sprite: deckBoards(0).sprite(DECK), variants: [deckBoards(1).sprite(DECK)] },
  { id: 'ceramica-banheiro', name: 'Cerâmica de banheiro', sprite: bathroomTile().sprite({ w: 'white2', W: 'white3', x: 'white1' }) },
  { id: 'piso-xadrez', name: 'Piso xadrez', sprite: checker().sprite({ K: 'black2', L: 'white2', k: 'black3', W: 'white3' }) },
  { id: 'piso-servidor', name: 'Piso de servidor', sprite: serverFloor().sprite({ m: 'metal1', M: 'metal3', n: 'metal0', k: 'black0' }) },
  {
    id: 'carpete-azul',
    name: 'Carpete azul',
    sprite: carpet(0).sprite({ c: 'fabricBlue2', l: 'fabricBlue3', d: 'fabricBlue1' }),
    variants: [carpet(1).sprite({ c: 'fabricBlue2', l: 'fabricBlue3', d: 'fabricBlue1' })],
  },
];

export const TILE_BY_ID: ReadonlyMap<string, TileDef> = new Map(TILES.map((t) => [t.id, t]));
